import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { EmptyState } from '@/components/empty-state'
import { api } from '@/lib/api'
import { fmtDateTime } from '@/lib/labels'

type AuditRow = {
  id: string
  action: string
  actorId: string | null
  actorName?: string | null
  targetId: string | null
  meta: unknown
  at: string
}

type FilterId = 'all' | 'orders' | 'payments' | 'stocks' | 'access'

const FILTERS: Array<{ id: FilterId; label: string }> = [
  { id: 'all', label: 'Tout' },
  { id: 'orders', label: 'Commandes' },
  { id: 'payments', label: 'Paiements' },
  { id: 'stocks', label: 'Stocks' },
  { id: 'access', label: 'Accès' },
]

function matchesFilter(action: string, filter: FilterId) {
  if (filter === 'all') return true
  const a = action.toLowerCase()
  if (filter === 'orders') return a.includes('order.')
  if (filter === 'payments') return a.includes('payment.')
  if (filter === 'stocks') return a.includes('stock.') || a.includes('inventory.')
  if (filter === 'access') return a.includes('auth.') || a.includes('staff.')
  return true
}

export function Journal() {
  const [filter, setFilter] = useState<FilterId>('all')
  const audit = useQuery({
    queryKey: ['admin-audit'],
    queryFn: () => api<{ items: AuditRow[] }>('/admin/audit?limit=100'),
  })

  const items = useMemo(() => {
    const all = audit.data?.items ?? []
    return all.filter((row) => matchesFilter(row.action, filter))
  }, [audit.data?.items, filter])

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`kicker rounded-pilule px-3 py-1.5 ${
              filter === f.id
                ? 'bg-encre-admin text-creme'
                : 'border border-bordure-admin text-encre-admin'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {audit.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState title="Journal vide" body="Les actions admin seront journalisées ici." />
      ) : (
        <section className="carte-admin overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Quand', 'Action', 'Acteur', 'Cible'].map((h) => (
                  <th key={h} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-bordure-admin last:border-0">
                  <td className="px-4 py-2.5 text-second-admin whitespace-nowrap">
                    {fmtDateTime(a.at)}
                  </td>
                  <td className="px-4 py-2.5 font-medium">{a.action}</td>
                  <td className="px-4 py-2.5 text-second-admin">
                    {a.actorName?.trim() || a.actorId || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-second-admin">{a.targetId ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  )
}
